import { AssetSchema, SystemType } from '@innovyze/lib_asset_schema';
import { useIsFeatureEnabled } from '@innovyze/stylovyze';
import { useMemo } from 'react';

interface AssetSchemaFF {
	[systemType: string]: boolean | string[];
}

// Shared rather than an inline `{}` so an unset flag keeps the same identity
// across renders - a fresh object each time would defeat the memo below.
const NO_RESTRICTION: AssetSchemaFF = {};

export const useAssetSchemaFF = () => {
	// Enabled system/asset types come straight from i360-release-asset-schema,
	// the same flag app-am-risk, app-am-activities and app-am-data-management
	// read. I360A-2514 removed the info360-rehab-new-asset-types gate and its
	// hard-coded fallback allow-list now discovery is fully schema-driven.
	// An unset flag yields {}, which leaves every system/asset type valid.
	const assetSchemaFF =
		(useIsFeatureEnabled(
			'i360-release-asset-schema',
		) as unknown as AssetSchemaFF) ?? NO_RESTRICTION;

	return useMemo(() => {
		const validSystemTypes = Object.keys(assetSchemaFF).filter(
			systemType =>
				assetSchemaFF[systemType] === true ||
				Array.isArray(assetSchemaFF[systemType]),
		);

		const allSystemTypesValid = validSystemTypes.length === 0;

		return {
			isValidSystemType: (systemType: SystemType) =>
				validSystemTypes.includes(systemType.id) || allSystemTypesValid,

			isValidAssetType: (assetType: AssetSchema) => {
				if (allSystemTypesValid) {
					return true;
				}
				const assetTypes = assetSchemaFF[assetType.systemType.id];
				if (assetTypes === true) {
					return true;
				}
				if (Array.isArray(assetTypes)) {
					return assetTypes.includes(assetType.id);
				}
				return false;
			},
		};
	}, [assetSchemaFF]);
};
